import PageShell from '@/components/feature/PageShell';
import Seo from '@/components/base/Seo';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import ContactForm from '@/pages/contact/components/ContactForm';
import { site } from '@/mocks/site';

const checklist = [
  '事件发生的大致时间与所在城市',
  '涉及的岗位类型与用工形式（全职、外包、实习等）',
  '你手上已有的材料类型，例如合同、工资条、聊天记录',
  '你希望被核实、被公开或暂时保留的部分',
];

export default function ContactPage() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: '联系我们', item: `${origin}/contact` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: '联系我们 - 职镜观察',
      description: '通过投稿与联系表单向职镜观察提供线索、提交更正说明或反馈内容建议。',
      url: `${origin}/contact`,
      inLanguage: 'zh-CN',
    },
  ];

  return (
    <PageShell>
      <Seo
        title="联系我们 | 爆料投稿、更正申明与内容建议 - 职镜观察"
        description="通过职镜观察的投稿与联系表单，提供可查证的职场线索、提交内容更正说明或反馈建议。我们默认对投稿人身份做脱敏处理，工作日48小时内回复。"
        keywords="职场爆料投稿,联系职镜观察,内容更正,线索投稿"
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100 paper-grain">
        <div className="mx-auto max-w-content px-4 pb-10 pt-8 md:px-6 md:pb-14 md:pt-10">
          <Breadcrumbs items={[{ name: '首页', path: '/' }, { name: '联系我们' }]} />
          <p className="font-label text-xs tracking-[0.28em] text-primary-600">TIP-OFF &amp; CONTACT</p>
          <h1 className="mt-3 max-w-3xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            联系我们：把你知道的细节，交给愿意核对的人
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是爆料投稿、事实更正还是内容建议，都可以通过下面的表单提交。
            我们会在核对公开信息后整理发布，并在需要时与你确认细节。
          </p>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <div className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
              <dl className="mt-4 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-base" />
                  </span>
                  <div>
                    <dt className="font-label text-xs text-foreground-500">投稿与联系邮箱</dt>
                    <dd className="mt-0.5">
                      <a
                        href={`mailto:${site.email}`}
                        className="font-label text-sm text-foreground-900 transition-colors hover:text-primary-700"
                      >
                        {site.email}
                      </a>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-time-line text-base" />
                  </span>
                  <div>
                    <dt className="font-label text-xs text-foreground-500">回复时间</dt>
                    <dd className="mt-0.5 font-label text-sm text-foreground-900">{site.replyTime}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-shield-user-line text-base" />
                  </span>
                  <div>
                    <dt className="font-label text-xs text-foreground-500">身份保护</dt>
                    <dd className="mt-0.5 font-label text-sm text-foreground-900">
                      默认匿名处理，不公开个人信息
                    </dd>
                  </div>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">投稿时可以附带的信息</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                不必一次写全，但以下信息越完整，核对的速度就越快：
              </p>
              <ul className="mt-4 space-y-3">
                {checklist.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-foreground-950 p-5 md:p-6">
              <i className="ri-information-line text-2xl text-accent-400" />
              <h2 className="mt-3 font-heading text-base font-bold text-background-50">两点说明</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-300">
                我们不会接受任何形式的付费删帖或付费正面宣传；同时，涉及个人隐私与未经核实的指控，
                在整理时会被谨慎处理或不予发布。
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <ContactForm />
        </div>
      </div>
    </PageShell>
  );
}